import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Cursor } from '@/components/atoms';
import { AlertBanner } from '@/components/atoms/AlertBanner';
import {
  ContactSection,
  CookieConsent,
  NavigationBar,
  NavigationMenu,
} from '@/components/organisms';
import { useCursor } from '@/contexts/CursorContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLenis } from '@/contexts/LenisContext';
import { useAlertBanner } from '@/hooks/useAlertBanner';
import { usePageTracking } from '@/hooks/usePageTracking';
import { useScrollToHash } from '@/hooks/useScrollToHash';

const Layout = ({ children }: { children: React.ReactNode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLogoVisible, setIsLogoVisible] = useState(true);
  const { setState: setCursorState } = useCursor();
  const { lenis } = useLenis();
  const location = useLocation();
  const { language } = useLanguage();
  const { banner, dismissed, dismiss } = useAlertBanner(location.pathname);
  useScrollToHash();
  usePageTracking();

  // Lock scroll avec Lenis au lieu de body.overflow
  useEffect(() => {
    if (lenis) {
      if (isMenuOpen) {
        lenis.stop();
      } else {
        lenis.start();
      }
    }
  }, [isMenuOpen, lenis]);

  // Reset cursor and scroll to top on route change
  useEffect(() => {
    setCursorState('default');

    if (lenis) {
      lenis.scrollTo(0, {
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      });
    }
  }, [location.pathname, lenis, setCursorState]);

  // Lenis / logo scroll
  useEffect(() => {
    if (!lenis) return;

    const handleScroll = (e) => {
      setIsLogoVisible(e.scroll < 250);
    };

    lenis.on('scroll', handleScroll);

    return () => {
      lenis.off('scroll', handleScroll);
    };
  }, [lenis]);

  return (
    <>
      {banner && !dismissed && (
        <AlertBanner
          text={language === 'fr' ? banner.textFR : banner.textEN}
          linkUrl={banner.linkUrl}
          linkLabel={
            language === 'fr' ? banner.linkLabelFR : banner.linkLabelEN
          }
          variant={banner.variant}
          onDismiss={dismiss}
        />
      )}

      <NavigationBar
        isLogoVisible={isLogoVisible}
        isMenuOpen={isMenuOpen}
        onToggleMenu={() => setIsMenuOpen((v) => !v)}
      />

      <NavigationMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
      />

      {children}
      <CookieConsent />
      <Cursor />
      <ContactSection />
    </>
  );
};

export default Layout;
